import { Show, createEffect, createMemo, lazy, onMount } from "solid-js";
import {
  Router,
  Route,
  useLocation,
  useNavigate,
  type RouteSectionProps,
} from "@solidjs/router";
import { AppShell } from "@/components/AppShell";
import { StateBlock } from "@/components/ui/StateBlock";
import { LinkButton } from "@/components/ui/Button";
import { authStore } from "@/auth/store";
import { gaTrackPageView } from "@/lib/googleAnalytics";
import { replaceWith } from "@/lib/navigation";
import AuthLogin from "@/routes/AuthLogin";
import TanStackProbe from "@/routes/TanStackProbe";

const MarketingHome = lazy(() => import("@/routes/MarketingHome"));
const About = lazy(() => import("@/routes/About"));
const Privacy = lazy(() => import("@/routes/Privacy"));
const Terms = lazy(() => import("@/routes/Terms"));
const AuthForgotPassword = lazy(() => import("@/routes/AuthForgotPassword"));
const AuthResetPassword = lazy(() => import("@/routes/AuthResetPassword"));
const AppHome = lazy(() => import("@/routes/AppHome"));
const AccountSecurity = lazy(() => import("@/routes/account/AccountSecurity"));
const AdminHome = lazy(() => import("@/routes/admin/AdminHome"));
const AdminApiProbe = lazy(() => import("@/routes/admin/ApiProbe"));
const TenantsList = lazy(() => import("@/routes/admin/TenantsList"));
const TenantNew = lazy(() => import("@/routes/admin/TenantNew"));
const TenantDetail = lazy(() => import("@/routes/admin/TenantDetail"));
const UserInvite = lazy(() => import("@/routes/admin/UserInvite"));
const UserDetail = lazy(() => import("@/routes/admin/UserDetail"));
const AuditLog = lazy(() => import("@/routes/admin/AuditLog"));
const RegistryHome = lazy(() => import("@/routes/registry/RegistryHome"));
const SitesList = lazy(() => import("@/routes/registry/SitesList"));
const SiteDetail = lazy(() => import("@/routes/registry/SiteDetail"));
const AssetsList = lazy(() => import("@/routes/registry/AssetsList"));
const AssetDetail = lazy(() => import("@/routes/registry/AssetDetail"));
const DevicesList = lazy(() => import("@/routes/registry/DevicesList"));
const DeviceDetail = lazy(() => import("@/routes/registry/DeviceDetail"));
const DashboardHome = lazy(() => import("@/routes/dashboard/DashboardHome"));
const MapView = lazy(() => import("@/routes/dashboard/MapView"));
const AlertsList = lazy(() => import("@/routes/operations/AlertsList"));
const AlertDetail = lazy(() => import("@/routes/operations/AlertDetail"));
const WorkBoard = lazy(() => import("@/routes/operations/WorkBoard"));
const WorkItemDetail = lazy(() => import("@/routes/operations/WorkItemDetail"));
const IntegrationsAdmin = lazy(
  () => import("@/routes/operations/IntegrationsAdmin"),
);

function WorkspaceNotFound() {
  const navigate = useNavigate();
  onMount(() => {
    if (!authStore.isAuthenticated()) {
      navigate("/", { replace: true });
    }
  });
  return (
    <StateBlock variant="empty" title="Workspace page not found">
      <div class="space-y-3">
        <p>This route does not exist in the workspace.</p>
        <LinkButton href="/app" variant="outline" size="sm">
          Return to workspace
        </LinkButton>
      </div>
    </StateBlock>
  );
}

/** Legacy URL from smoke tests / bookmarks → canonical route. */
function RedirectToAlerts() {
  onMount(() => {
    replaceWith("/alerts");
  });
  return null;
}

/** Full-viewport routes without AppShell sidebar (marketing, probes, sign-in). */
const SHELL_BYPASS_PATHS = [
  "/",
  "/about",
  "/privacy",
  "/terms",
  "/auth/login",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/__tanstack-probe",
];

function Root(props: RouteSectionProps) {
  const location = useLocation();
  createEffect(() => {
    gaTrackPageView(`${location.pathname}${location.search}`);
  });

  const shellBypass = createMemo(() =>
    SHELL_BYPASS_PATHS.includes(location.pathname),
  );

  return (
    <Show
      when={shellBypass()}
      keyed
      fallback={<AppShell>{props.children}</AppShell>}
    >
      <div class="min-h-screen">{props.children}</div>
    </Show>
  );
}

export function App() {
  return (
    <Router root={Root}>
      <Route path="/" component={MarketingHome} />
      <Route path="/about" component={About} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/app" component={AppHome} />
      <Route path="/app/account/security" component={AccountSecurity} />
      <Route path="/__tanstack-probe" component={TanStackProbe} />
      <Route path="/auth/login" component={AuthLogin} />
      <Route path="/auth/forgot-password" component={AuthForgotPassword} />
      <Route path="/auth/reset-password" component={AuthResetPassword} />
      <Route path="/app/admin" component={AdminHome} />
      <Route path="/app/admin/api-probe" component={AdminApiProbe} />
      <Route path="/app/admin/tenants" component={TenantsList} />
      <Route path="/app/admin/tenants/new" component={TenantNew} />
      <Route path="/app/admin/tenants/:tenantId" component={TenantDetail} />
      <Route path="/app/admin/tenants/:tenantId/users/new" component={UserInvite} />
      <Route path="/app/admin/tenants/:tenantId/users/:userId" component={UserDetail} />
      <Route path="/app/admin/audit" component={AuditLog} />
      <Route path="/registry" component={RegistryHome} />
      <Route path="/registry/sites" component={SitesList} />
      <Route path="/registry/sites/:siteId" component={SiteDetail} />
      <Route path="/registry/assets" component={AssetsList} />
      <Route path="/registry/assets/:assetId" component={AssetDetail} />
      <Route path="/registry/devices" component={DevicesList} />
      <Route path="/registry/devices/:deviceId" component={DeviceDetail} />
      <Route path="/dashboard" component={DashboardHome} />
      <Route path="/dashboard/map" component={MapView} />
      <Route path="/alerts" component={AlertsList} />
      <Route path="/alerts/:alertId" component={AlertDetail} />
      <Route path="/work" component={WorkBoard} />
      <Route path="/work/:workItemId" component={WorkItemDetail} />
      <Route path="/integrations" component={IntegrationsAdmin} />
      {/* Mirrors under /app/* — bare paths like /app/dashboard otherwise hit `*` and redirect to marketing. */}
      <Route path="/app/dashboard" component={DashboardHome} />
      <Route path="/app/dashboard/map" component={MapView} />
      <Route path="/app/alerts" component={AlertsList} />
      <Route path="/app/alerts/:alertId" component={AlertDetail} />
      <Route path="/app/work" component={WorkBoard} />
      <Route path="/app/work/:workItemId" component={WorkItemDetail} />
      <Route path="/app/integrations" component={IntegrationsAdmin} />
      <Route path="/app/registry" component={RegistryHome} />
      <Route path="/app/registry/sites" component={SitesList} />
      <Route path="/app/registry/sites/:siteId" component={SiteDetail} />
      <Route path="/app/registry/assets" component={AssetsList} />
      <Route path="/app/registry/assets/:assetId" component={AssetDetail} />
      <Route path="/app/registry/devices" component={DevicesList} />
      <Route path="/app/registry/devices/:deviceId" component={DeviceDetail} />
      <Route path="/app/operations/alerts" component={RedirectToAlerts} />
      <Route path="*" component={WorkspaceNotFound} />
    </Router>
  );
}
